import { Box, Grid } from '@material-ui/core';
import * as React from 'react';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import { adjustTextColor } from '../../../Palette/adjustTextColor';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';
import NotEnoughTagGroups from '../../NotEnoughTagGroups';

import CILegend from './CILegend';
import { TableCellItem } from './TableCellItem';
import { trafficIntervals } from './TrafficIntervals';

export interface CompetitiveIntelligenceProps extends ChartProps { }

const CompetitiveIntelligence: React.FC<CompetitiveIntelligenceProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showTagGroupIds: true, showDomainIds: true },
      })),
    []
  );

  // Fetch data
  const cIQuery = projectsApiService.queries.analysis.useGetCompetitiveIntelligence({
    projectId: project.id,
    tagGroupIds: analysisFilterState?.tagGroupIds,
    domainIds: analysisFilterState?.domainIds,
  });

  const cILoading = cIQuery?.query?.loading;
  const cIResult = cIQuery?.result?.tagGroupAnalyses;

  // Structure data
  const clientFirstData = cIResult?.map((tg) => ({
    tagGroupId: tg.tagGroupId,
    tagGroupName: tg.tagGroupName,
    tagGroupTraffic: tg.tagGroupTraffic,
    maxValue: Math.max(...tg.domainAnalyses.map((di) => di.traffic)),
    minValue: Math.min(...tg.domainAnalyses.map((di) => di.traffic)),
    domainAnalyses: tg.domainAnalyses.reduce((acc, item) => {
      if (item.domain.type === 'PROJECT') {
        return [item, ...acc];
      }
      return [...acc, item];
    }, []),
  }));

  //Styling
  const intervalScheme = trafficIntervals(palette);
  const textColor = adjustTextColor(palette, 0.85, 0);

  // Render
  if (didQueryTimeout(cIQuery?.query)) {
    return <DataTimedOut />;
  }

  if (cILoading) {
    return <AnalysisChartLoading />;
  }

  if (!analysisFilterState?.tagGroupIds?.length) {
    return <NotEnoughTagGroups />;
  }

  if (!cIResult?.some((tg) => tg.domainAnalyses.some((da) => da.traffic > 0))) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="w-full flex justify-center">
        <CILegend language={language} palette={palette} />
      </div>
      <div className="pt-8">
        <Grid container direction={'row'} wrap={'nowrap'}>
          <Grid item xs />
          {clientFirstData?.map((tg, index) => (
            <Grid item xs zeroMinWidth key={index}>
              <div className="pb-3 text-[14px] text-center font-regular text-gray-800 text-ellipsis overflow-hidden">
                {tg.tagGroupName}
              </div>
            </Grid>
          ))}
        </Grid>
        {clientFirstData[0]?.domainAnalyses.map((d, domainIndex) => (
          <Grid container direction={'row'} wrap={'nowrap'} alignItems={'center'} key={domainIndex}>
            <Grid item xs zeroMinWidth>
              <div className="pr-4 text-[13px] text-right font-medium text-gray-800">
                {d.domain.name}
                {d.domain.type === 'CLIENT' &&
                  ` (${internationalText.chart.competitors_by_topic.client[language]})`}
              </div>
            </Grid>
            {clientFirstData?.map((tg, tgIndex) => (
              <Grid item xs key={tgIndex}>
                <TableCellItem
                  value={tg.domainAnalyses[domainIndex].traffic}
                  total={tg.tagGroupTraffic}
                  minValue={tg.minValue}
                  maxValue={tg.maxValue}
                  intervals={intervalScheme}
                  textColor={textColor}
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </div>
    </div>
  );
};

export default CompetitiveIntelligence;
